import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, GraduationCap, TrendingUp, AlertTriangle, CheckCircle,
  Clock, XCircle, ArrowUpRight, ArrowRight, Zap, DollarSign, BarChart3, Globe
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../api';

const PIE_COLORS = ['#516EFA', '#14C8C4', '#F59E0B', '#8B5CF6', '#EF4444'];
const STATUS_COLORS = {
  active: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  trial:  'bg-amber-50 text-amber-700 border border-amber-200',
  suspended: 'bg-red-50 text-red-700 border border-red-200',
  expired: 'bg-slate-100 text-slate-500 border border-slate-200',
};

const fmt = (n) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(n);

export default function MasterDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/master/dashboard').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="p-8 space-y-6">
      {[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-slate-100 rounded-2xl animate-pulse" />)}
    </div>
  );

  const stats = data?.stats || {};
  const recentSchools = data?.recent_schools || [];
  const planDist = data?.plan_distribution || [];
  const monthlySig = data?.monthly_signups || [];
  const revByPlan = data?.revenue_by_plan || [];

  const kpis = [
    {
      label: 'Monthly Revenue',
      value: fmt(stats.mrr || 0),
      sub: `ARR: ${fmt(stats.arr || 0)}`,
      icon: DollarSign,
      bg: 'bg-[#516EFA]',
      change: '+0%',
    },
    {
      label: 'Total Schools',
      value: stats.total_schools || 0,
      sub: `${stats.active_schools || 0} active · ${stats.trial_schools || 0} trial`,
      icon: Building2,
      bg: 'bg-[#14C8C4]',
      change: null,
    },
    {
      label: 'Platform Users',
      value: (stats.total_students || 0) + (stats.total_teachers || 0),
      sub: `${(stats.total_students || 0).toLocaleString()} students · ${(stats.total_teachers || 0).toLocaleString()} staff`,
      icon: Users,
      bg: 'bg-purple-500',
      change: null,
    },
    {
      label: 'Expiring Soon',
      value: stats.expiring_count || 0,
      sub: 'Next 30 days',
      icon: AlertTriangle,
      bg: stats.expiring_count > 0 ? 'bg-amber-500' : 'bg-slate-400',
      change: null,
      alert: stats.expiring_count > 0,
    },
  ];

  return (
    <div className="p-6 lg:p-8 space-y-7 animate-fade-in" data-testid="master-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-slate-900">Platform Overview</h1>
          <p className="text-slate-500 text-sm mt-0.5">EDUVA Master Admin · {new Date().toLocaleDateString('en-NG', { month: 'long', year: 'numeric' })}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="h-9 text-sm" onClick={() => navigate('/master/schools')}>
            <Building2 className="w-4 h-4 mr-2" />Schools
          </Button>
          <Button className="h-9 text-sm bg-[#516EFA] hover:bg-[#4058e8] text-white" onClick={() => navigate('/master/revenue')}>
            <BarChart3 className="w-4 h-4 mr-2" />Revenue
          </Button>
        </div>
      </div>

      {/* Expiring Alert Banner */}
      {stats.expiring_count > 0 && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3.5">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            <strong>{stats.expiring_count} school(s)</strong> have subscriptions expiring in the next 30 days.
          </p>
          <Button variant="outline" size="sm" className="ml-auto border-amber-300 text-amber-700 hover:bg-amber-100 h-7 text-xs" onClick={() => navigate('/master/schools')}>
            Review <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      )}
      {stats.suspended_schools > 0 && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-5 py-3.5">
          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-800"><strong>{stats.suspended_schools} school(s)</strong> are currently suspended.</p>
          <Button variant="outline" size="sm" className="ml-auto border-red-300 text-red-600 hover:bg-red-50 h-7 text-xs" onClick={() => navigate('/master/schools')}>
            Review
          </Button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => (
          <div key={i} className={`bg-white rounded-2xl p-5 border ${k.alert ? 'border-amber-200' : 'border-slate-100'} hover:shadow-md transition-shadow`} data-testid={`kpi-${i}`}>
            <div className="flex items-start justify-between mb-4">
              <div className={`w-9 h-9 rounded-xl ${k.bg} flex items-center justify-center`}>
                <k.icon className="w-4 h-4 text-white" />
              </div>
              {k.change && (
                <span className="flex items-center gap-0.5 text-xs text-emerald-600 font-medium">
                  <ArrowUpRight className="w-3 h-3" />{k.change}
                </span>
              )}
            </div>
            <p className="font-outfit text-2xl font-bold text-slate-900">{typeof k.value === 'number' ? k.value.toLocaleString() : k.value}</p>
            <p className="text-xs font-medium text-slate-600 mt-1">{k.label}</p>
            <p className="text-[11px] text-slate-400 mt-0.5">{k.sub}</p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* School Registrations */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-slate-100">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-outfit font-semibold text-slate-900">School Registrations</h3>
            <Badge className="bg-slate-100 text-slate-500 text-xs">{stats.total_schools} total</Badge>
          </div>
          {monthlySig.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={monthlySig}>
                <defs>
                  <linearGradient id="cGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#516EFA" stopOpacity={0.12} />
                    <stop offset="95%" stopColor="#516EFA" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: 12 }} />
                <Area type="monotone" dataKey="count" name="Schools" stroke="#516EFA" strokeWidth={2} fill="url(#cGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-slate-300 text-sm">No registration data yet</div>
          )}
        </div>

        {/* Plan Distribution */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <h3 className="font-outfit font-semibold text-slate-900 mb-5">By Plan</h3>
          {planDist.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie data={planDist} dataKey="count" nameKey="_id" cx="50%" cy="50%" outerRadius={65} innerRadius={35} strokeWidth={0}>
                    {planDist.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-3">
                {planDist.slice(0, 5).map((p, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-slate-600 capitalize">{p._id || 'unknown'}</span>
                    </div>
                    <span className="font-semibold text-slate-800">{p.count}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-40 flex items-center justify-center text-slate-300 text-sm">No data</div>
          )}
        </div>
      </div>

      {/* Revenue by Plan + Recent Schools */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Revenue by Plan Bar Chart */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-outfit font-semibold text-slate-900">Revenue by Plan</h3>
            <button onClick={() => navigate('/master/revenue')} className="text-xs text-[#516EFA] hover:underline flex items-center gap-1">
              Full report <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          {revByPlan.filter(r => r.mrr > 0).length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={revByPlan} barSize={28}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="plan" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `₦${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(v) => [`₦${Number(v).toLocaleString()}`, 'MRR']}
                  contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', fontSize: 12 }}
                />
                <Bar dataKey="mrr" fill="#516EFA" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-44 flex flex-col items-center justify-center text-slate-300">
              <DollarSign className="w-8 h-8 mb-2" />
              <p className="text-sm">No paid subscriptions yet</p>
            </div>
          )}
        </div>

        {/* Recent Schools */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-outfit font-semibold text-slate-900">Recent Schools</h3>
            <button onClick={() => navigate('/master/schools')} className="text-xs text-[#516EFA] hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          {recentSchools.length === 0 ? (
            <div className="py-12 text-center">
              <Building2 className="w-7 h-7 mx-auto mb-2 text-slate-300" />
              <p className="text-sm text-slate-400">No schools yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {recentSchools.map((s, i) => (
                <div key={i} className="px-6 py-3.5 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#eef1ff] flex items-center justify-center flex-shrink-0">
                      <Globe className="w-4 h-4 text-[#516EFA]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{s.name}</p>
                      <p className="text-[11px] text-slate-400">{s.city}, {s.state}</p>
                    </div>
                  </div>
                  <Badge className={`text-[10px] ${STATUS_COLORS[s.status] || ''}`}>{s.status}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid sm:grid-cols-3 gap-4">
        {[
          { icon: Building2, label: 'Manage Schools', sub: 'View, upgrade, disable schools', path: '/master/schools', color: '#516EFA' },
          { icon: TrendingUp, label: 'Revenue & Billing', sub: 'MRR, ARR and payment history', path: '/master/revenue', color: '#14C8C4' },
          { icon: Zap, label: 'Broadcast Message', sub: 'Send announcement to all schools', path: '/master/system', color: '#F59E0B' },
        ].map((a, i) => (
          <button key={i} onClick={() => navigate(a.path)} className="bg-white rounded-2xl p-5 border border-slate-100 text-left hover:border-slate-200 hover:shadow-md transition-all group">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: `${a.color}18` }}>
              <a.icon className="w-4 h-4" style={{ color: a.color }} />
            </div>
            <p className="font-semibold text-sm text-slate-800">{a.label}</p>
            <p className="text-xs text-slate-400 mt-0.5">{a.sub}</p>
            <ArrowRight className="w-3.5 h-3.5 mt-3 text-slate-300 group-hover:text-slate-500 transition-colors" />
          </button>
        ))}
      </div>
    </div>
  );
}
